import { forwardRef, useImperativeHandle, useRef } from "react";
import type { ReactNode } from "react";
import { Popover } from "../../../../components/Popover";
import { MentionList, MentionListRef } from "./MentionList";
import type { RichTextEditorMentionOption } from "../types";

export type MentionPopoverRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

type MentionPopoverProps = {
  open: boolean;
  rect: DOMRect | null;
  items: RichTextEditorMentionOption[];
  command: ((item: RichTextEditorMentionOption) => void) | null;
  renderOption?: (option: RichTextEditorMentionOption) => ReactNode;
};

export const MentionPopover = forwardRef<
  MentionPopoverRef,
  MentionPopoverProps
>(({ open, rect, items, command, renderOption }, ref) => {
  const listRef = useRef<MentionListRef>(null);

  useImperativeHandle(ref, () => ({
    onKeyDown: (props) => listRef.current?.onKeyDown(props) ?? false,
  }));

  return (
    <Popover open={open} disableCaret noPadding placement="bottom-start">
      <Popover.Trigger>
        {({ ref: anchorRef }) => (
          <button
            ref={anchorRef}
            type="button"
            tabIndex={-1}
            aria-hidden
            style={{
              position: "fixed",
              top: rect?.bottom ?? 0,
              left: rect?.left ?? 0,
              width: rect?.width ?? 0,
              height: 0,
              visibility: "hidden",
              pointerEvents: "none",
            }}
          />
        )}
      </Popover.Trigger>
      <Popover.Content>
        {command && (
          <MentionList
            ref={listRef}
            items={items}
            command={command}
            renderOption={renderOption}
          />
        )}
      </Popover.Content>
    </Popover>
  );
});

MentionPopover.displayName = "MentionPopover";
